import "./SectionUsersSidebarPanel.scss";

import { compareDesc } from "date-fns";
import { partition, sortBy } from "lodash-es";
import { type FC, type ReactNode, useCallback, useMemo } from "react";

import { CourseUserItem } from "./SectionUserItem";

import { getActiveStateSortKeyForUser } from "components/materials/section/helpers";
import { t } from "i18n/i18n";
import { AccordionList } from "mds/components/AccordionList";
import { IconText } from "mds/components/IconText";
import { PersonSingleIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  EnhancedUserWithSectionActivity,
  selectActiveClassSessionCategory,
  selectCurrentUserIsMPAdmin,
  selectScreensharingClassUserId,
  selectUserIdByClassUserId,
} from "store/selectors";
import { getUserLastNameSortKey } from "store/selectors/sorting";
import { usePresentation } from "utils/presentation";

interface DisabledUsersCardProps {
  users: EnhancedUserWithSectionActivity[];
  className?: string;
}

export const DisabledUsersCard: FC<DisabledUsersCardProps> = ({ users, className }) => {
  return (
    <CourseUserCard
      className={className}
      headerCount={users.length.toString()}
      headerIcon={<PersonSingleIcon size="xs" />}
      headerTitle={t("user_options_menu.suspended_accounts")}
      users={users}
      canModifyUsers
    />
  );
};

interface CourseUserCardProps {
  users: EnhancedUserWithSectionActivity[];
  className?: string;
  canModifyUsers?: boolean;
  headerTitle: string;
  headerCount: string;
  headerIcon: ReactNode;
  separateUsersInClassSession?: boolean;
  showEmpty?: boolean;
  startOpen?: boolean;
  filterUsers?: (users: EnhancedUserWithSectionActivity[]) => EnhancedUserWithSectionActivity[];
}

export const CourseUserCard: FC<CourseUserCardProps> = ({
  users,
  className,
  canModifyUsers = false,
  headerTitle,
  headerCount,
  headerIcon,
  separateUsersInClassSession = false,
  showEmpty = false,
  startOpen = false,
  filterUsers = (u: EnhancedUserWithSectionActivity[]) => u,
}) => {
  const { presentingUserId } = usePresentation();
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const screensharingUserId = useAppSelector((state) =>
    selectUserIdByClassUserId(state, screensharingClassUserId),
  );
  const classSessionCategory = useAppSelector(selectActiveClassSessionCategory);
  const isMpAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const canImpersonate = isMpAdmin;

  const filteredUsers = filterUsers(users);

  // For virtual and in-person class sessions, students (handled by the `separateUsersInClassSession` prop)
  // are split into two lists: those who have joined the class session and those who have not
  let usersInClass: EnhancedUserWithSectionActivity[];
  let usersNotInClass: EnhancedUserWithSectionActivity[];
  if (classSessionCategory && separateUsersInClassSession) {
    [usersInClass, usersNotInClass] = partition(filteredUsers, (user) => user.is_in_class_session);
  } else {
    usersInClass = filteredUsers;
    usersNotInClass = [];
  }

  const compareUsers = useCallback(
    (a: EnhancedUserWithSectionActivity, b: EnhancedUserWithSectionActivity) => {
      // Users are shown in this order:
      // 1. The user who is sharing their screen
      if (a.id === screensharingUserId) return -1;
      if (b.id === screensharingUserId) return 1;

      // 2. The user who is presenting
      if (a.id === presentingUserId) return -1;
      if (b.id === presentingUserId) return 1;

      // 3. Users who have raised their hand, ordered by hand_raised_at
      if (a.hand_raised_at && b.hand_raised_at) {
        return compareDesc(b.hand_raised_at, a.hand_raised_at);
      }
      if (a.hand_raised_at) return -1;
      if (b.hand_raised_at) return 1;

      // 4. Users who are following
      if (a.isFollowing && !b.isFollowing) return -1;
      if (!a.isFollowing && b.isFollowing) return 1;

      // (The rest is handled below by getActiveStateSortKeyForUser)
      // 5. Users who are online but not presenting or following
      // 6. Users who are offline
      return 0;
    },
    [presentingUserId, screensharingUserId],
  );

  // These hashes need to include every field that is used in the sorting function in order for
  // sortedUsersInClass and sortedUsersNotInClass to get re-sorted when necessary
  const usersInClassHash = usersInClass
    .map(
      (user) =>
        `${user.id}-${user.last_name}-${user.courseActivityStatus}-${user.hand_raised_at || ""}-${user.isFollowing}`,
    )
    .join(",");
  const usersNotInClassHash = usersNotInClass
    .map(
      (user) =>
        `${user.id}-${user.last_name}-${user.courseActivityStatus}-${user.hand_raised_at || ""}-${user.isFollowing}`,
    )
    .join(",");

  const sortedUsersInClass = useMemo(
    () =>
      sortBy(usersInClass, [getActiveStateSortKeyForUser, getUserLastNameSortKey]).toSorted(
        compareUsers,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersInClassHash, compareUsers],
  );

  const sortedUsersNotInClass = useMemo(
    () =>
      sortBy(usersNotInClass, [getActiveStateSortKeyForUser, getUserLastNameSortKey]).toSorted(
        compareUsers,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersNotInClassHash, compareUsers],
  );

  if (!showEmpty && sortedUsersInClass.length === 0 && sortedUsersNotInClass.length === 0) {
    return null;
  }

  return (
    <AccordionList
      className={className}
      startOpen={startOpen}
      title={headerTitle}
      titleRightContent={
        <IconText
          className="gap-1 text-sm text-black-tint-20"
          iconEnd={headerIcon}
          text={headerCount}
        />
      }
    >
      {classSessionCategory && separateUsersInClassSession && sortedUsersInClass.length > 0 && (
        <div className="body-xs ml-1 mt-3 text-black-tint-40">{t("av.present_in_session")}</div>
      )}

      {sortedUsersInClass.map((user: EnhancedUserWithSectionActivity) => (
        <CourseUserItem
          canImpersonate={canImpersonate}
          canModifyUsers={canModifyUsers}
          key={user.user_id}
          presentingUserId={presentingUserId}
          screensharingUserId={screensharingUserId}
          userId={user.user_id}
        />
      ))}

      {classSessionCategory && separateUsersInClassSession && sortedUsersNotInClass.length > 0 && (
        <div className="body-xs ml-1 mt-3 text-black-tint-40">{t("av.not_present_in_session")}</div>
      )}

      {sortedUsersNotInClass.map((user: EnhancedUserWithSectionActivity) => (
        <CourseUserItem
          canImpersonate={canImpersonate}
          canModifyUsers={canModifyUsers}
          key={user.user_id}
          presentingUserId={presentingUserId}
          screensharingUserId={screensharingUserId}
          userId={user.user_id}
        />
      ))}
    </AccordionList>
  );
};
